type Side = 'T' | 'R';
type SetIndex = 1 | 2 | 3;
type ScoreField = `kul_${'t' | 'r'}_${'sonuc' | `set${SetIndex}` | `sonuc_set${SetIndex}`}`;
type ScoreSource = Partial<Record<ScoreField, unknown>> &
  Pick<
    Partial<Tournaments.Game>,
    | 'durum'
    | 'sonuc_tipi'
    | 'kazanan_id'
    | 'kullanici_t_id'
    | 'kullanici_r_id'
    | 'kullanici_t_partner_id'
    | 'kullanici_r_partner_id'
  >;

const numericScore = (value: unknown): number | null => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const number = Number(value);
  return Number.isFinite(number) && Number.isInteger(number) && number >= 0 ? number : null;
};

/** Current fields take precedence, including a legitimate zero; legacy fields are a fallback. */
export function tournamentSetScore(match: ScoreSource, side: Side, index: SetIndex): number | null {
  const prefix = side === 'T' ? 't' : 'r';
  return (
    numericScore(match[`kul_${prefix}_set${index}`]) ??
    numericScore(match[`kul_${prefix}_sonuc_set${index}`])
  );
}

export function getTournamentScore(match: ScoreSource) {
  const total = { left: numericScore(match.kul_t_sonuc), right: numericScore(match.kul_r_sonuc) };
  const hasTotal = (total.left ?? 0) > 0 || (total.right ?? 0) > 0;
  const status = match.durum || (hasTotal || match.kazanan_id ? 'COMPLETED' : 'SCHEDULED');
  const live = ['ON_COURT', 'SUSPENDED'].includes(status);
  const noPlay =
    ['WALKOVER', 'DEFAULTED', 'CANCELLED'].includes(status) ||
    ['WALKOVER', 'DEFAULT', 'NO_SHOW'].includes(match.sonuc_tipi || '');
  const allSets = ([1, 2, 3] as const).map(index => ({
    index,
    left: tournamentSetScore(match, 'T', index),
    right: tournamentSetScore(match, 'R', index),
  }));
  const lastRecorded = allSets.reduce(
    (last, set) => ((set.left ?? 0) > 0 || (set.right ?? 0) > 0 ? set.index : last),
    0,
  );
  const sets = noPlay
    ? []
    : allSets.filter(
        set =>
          (set.left ?? 0) > 0 ||
          (set.right ?? 0) > 0 ||
          (live && set.index === lastRecorded + 1 && (set.left !== null || set.right !== null)),
      );
  const leftWinner =
    !!match.kazanan_id &&
    [match.kullanici_t_id, match.kullanici_t_partner_id].includes(match.kazanan_id);
  const rightWinner =
    !!match.kazanan_id &&
    [match.kullanici_r_id, match.kullanici_r_partner_id].includes(match.kazanan_id);
  let winner: Side | null = leftWinner !== rightWinner ? (leftWinner ? 'T' : 'R') : null;
  if (
    !match.kazanan_id &&
    status === 'COMPLETED' &&
    !noPlay &&
    hasTotal &&
    total.left !== null &&
    total.right !== null &&
    total.left !== total.right
  ) {
    winner = total.left > total.right ? 'T' : 'R';
  }
  return { total, showTotal: !noPlay && (hasTotal || live), status, sets, winner, noPlay };
}
